/**
 * useUsers Hook
 *
 * Provides admin user listing with filtering, searching, and pagination
 * using real Firestore queries.
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export interface User {
  id: string;
  email: string;
  displayName: string;
  photoURL?: string;
  phone?: string;
  role: 'guest' | 'host' | 'admin';
  isSuspended?: boolean;
  suspendedAt?: Date;
  suspensionReason?: string;
  emailVerified?: boolean;
  createdAt: Date;
  lastLoginAt?: Date;
  /** Set when the user was imported from the legacy WordPress/SQL database */
  wpUserId?: string;
  /** True once a migrated user has claimed the account (first password set / login) */
  accountRetrieved?: boolean;
  accountRetrievedAt?: Date;
  retrievalMethod?: string;
  stats?: {
    listingsCount?: number;
    bookingsMade?: number;
    bookingsReceived?: number;
    reviewsGiven?: number;
    totalEarnings?: number;
  };
}

export interface UsersFilter {
  search?: string;
  role?: 'guest' | 'host' | 'admin' | '';
  status?: 'active' | 'suspended' | '';
  /** Legacy (SQL-migrated) account claim state */
  migration?: 'retrieved' | 'unretrieved' | '';
  sortBy?: 'createdAt' | 'displayName' | 'lastLoginAt';
  sortOrder?: 'asc' | 'desc';
}

const PAGE_SIZE = 10;

const toDate = (val: any): Date | undefined => {
  if (!val) return undefined;
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return new Date(val);
};

const mapUserDoc = (docSnap: any): User => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    email: data.email || '',
    displayName: data.displayName || data.firstName
      ? `${data.firstName || ''} ${data.lastName || ''}`.trim()
      : data.email || '',
    photoURL: data.photoURL,
    phone: data.phone,
    role: data.role || 'guest',
    isSuspended: data.isSuspended || false,
    suspendedAt: toDate(data.suspendedAt),
    suspensionReason: data.suspensionReason,
    emailVerified: data.isVerified ?? data.emailVerified,
    createdAt: toDate(data.createdAt) || new Date(),
    lastLoginAt: toDate(data.lastLoginAt),
    wpUserId: data.wpUserId,
    accountRetrieved: data.accountRetrieved || false,
    accountRetrievedAt: toDate(data.accountRetrievedAt),
    retrievalMethod: data.retrievalMethod,
    stats: data.stats,
  };
};

export const useUsers = (filter: UsersFilter, page: number = 0) => {
  const [users, setUsers] = useState<User[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const q = query(
          collection(db, 'users'),
          orderBy(filter.sortBy || 'createdAt', filter.sortOrder || 'desc')
        );
        const snapshot = await getDocs(q);
        let allUsers = snapshot.docs.map(mapUserDoc);

        // Client-side filtering
        if (filter.search) {
          const searchLower = filter.search.toLowerCase();
          allUsers = allUsers.filter(
            (u) =>
              u.displayName.toLowerCase().includes(searchLower) ||
              u.email.toLowerCase().includes(searchLower) ||
              u.phone?.toLowerCase().includes(searchLower)
          );
        }

        if (filter.role) {
          allUsers = allUsers.filter((u) => u.role === filter.role);
        }

        if (filter.status === 'suspended') {
          allUsers = allUsers.filter((u) => u.isSuspended);
        } else if (filter.status === 'active') {
          allUsers = allUsers.filter((u) => !u.isSuspended);
        }

        if (filter.migration === 'retrieved') {
          allUsers = allUsers.filter((u) => u.wpUserId && u.accountRetrieved);
        } else if (filter.migration === 'unretrieved') {
          allUsers = allUsers.filter((u) => u.wpUserId && !u.accountRetrieved);
        }

        if (!cancelled) {
          setTotalCount(allUsers.length);
          const start = page * PAGE_SIZE;
          setUsers(allUsers.slice(start, start + PAGE_SIZE));
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch users:', err);
        if (!cancelled) {
          setUsers([]);
          setTotalCount(0);
          setIsLoading(false);
        }
      }
    };

    fetchUsers();
    return () => { cancelled = true; };
  }, [filter.search, filter.role, filter.status, filter.migration, filter.sortBy, filter.sortOrder, page]);

  return {
    users,
    totalCount,
    isLoading,
    hasMore: (page + 1) * PAGE_SIZE < totalCount,
    totalPages: Math.ceil(totalCount / PAGE_SIZE),
  };
};

export const useUser = (userId: string) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchUser = async () => {
      setIsLoading(true);
      try {
        const docSnap = await getDoc(doc(db, 'users', userId));
        if (!cancelled) {
          if (docSnap.exists()) {
            setUser(mapUserDoc(docSnap));
          } else {
            setUser(null);
          }
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch user:', err);
        if (!cancelled) {
          setUser(null);
          setIsLoading(false);
        }
      }
    };

    if (userId) fetchUser();
    return () => { cancelled = true; };
  }, [userId]);

  return { user, isLoading };
};

export default useUsers;
